/**
 * Owner: Masa Bayu
 * File: ReaderProfileOverview.tsx
 * Date:Time: 2026-10-19 18:45
 * Description: §393 — seksyen Ringkasan Profil Reader: KPI nyata, karusel
 *   Teruskan Membaca (progress bar + CTA reader), notifikasi (store nyata).
 */

import type { ReactElement } from 'react';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import { markLandingAlertsRead } from '../home/marketplace/landing/landing-alerts';
import { safeIntlTag } from '../home/marketplace/marketplace-locale';
import type { ReaderProfileData } from './reader-profile-data';
import type { ReaderProfileLabels } from './reader-profile-labels';

function formatDate(at: number, interfaceTag: string): string {
  return new Date(at).toLocaleDateString(safeIntlTag(interfaceTag), {
    day: 'numeric',
    month: 'short',
  });
}

export function ReaderProfileOverview({
  data,
  labels,
  interfaceTag,
  onNavigate,
  onAlertsChanged,
}: {
  readonly data: ReaderProfileData;
  readonly labels: ReaderProfileLabels;
  readonly interfaceTag: string;
  readonly onNavigate: (path: string) => void;
  readonly onAlertsChanged: () => void;
}): ReactElement {
  const s = data.stats;
  const kpis: readonly { readonly label: string; readonly value: number }[] = [
    { label: labels.stats.owned, value: s.owned },
    { label: labels.stats.reading, value: s.reading },
    { label: labels.stats.completed, value: s.completed },
    { label: labels.stats.wishlist, value: s.wishlist },
    { label: labels.stats.bookmarks, value: s.bookmarks },
    { label: labels.stats.highlights, value: s.highlights },
    { label: labels.stats.follows, value: s.follows },
  ];
  const unread = data.alerts.filter((a) => !a.read).length;
  return (
    <div className="qxr__stack">
      <section className="qxr__card" aria-labelledby="qxr-kpi">
        <h2 id="qxr-kpi" className="qxr__section-title">
          <LandingIcon name="chart" size={18} /> {labels.title}
        </h2>
        <dl className="qxr__kpis">
          {kpis.map((k) => (
            <div key={k.label} className="qxr__kpi">
              <dt>{k.label}</dt>
              <dd>{k.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-continue">
        <div className="qxr__card-head">
          <h2 id="qxr-continue" className="qxr__section-title">
            <LandingIcon name="bookmark" size={18} /> {labels.continueReading}
          </h2>
          <button type="button" className="qxr__link" onClick={() => onNavigate('/library')}>
            {labels.seeAllLibrary} <LandingIcon name="chevronRight" size={12} />
          </button>
        </div>
        {data.continueReading.length === 0 ? (
          <p className="qxr__empty">{labels.emptyContinueReading}</p>
        ) : (
          <ul className="qxr__crlist">
            {data.continueReading.map((card) => (
              <li key={card.item.libraryItemId} className="qxr__cr">
                {card.coverUrl !== null ? (
                  <img className="qxr__crcover" src={card.coverUrl} alt="" loading="lazy" />
                ) : (
                  <span className="qxr__crcover qxr__crcover--none" aria-hidden="true">
                    <LandingIcon name="book" size={20} />
                  </span>
                )}
                <div className="qxr__crbody">
                  <strong className="qxr__crtitle">{card.item.title}</strong>
                  <div
                    className="qxr__bar"
                    role="progressbar"
                    aria-valuenow={card.item.progress.percentComplete}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={card.progressLabel}
                  >
                    <span style={{ width: `${String(card.item.progress.percentComplete)}%` }} />
                  </div>
                  <span className="qxr__muted">{card.progressLabel}</span>
                </div>
                <button
                  type="button"
                  className="qxr__cta"
                  onClick={() => onNavigate(`/read/${encodeURIComponent(card.item.publicationId)}`)}
                >
                  {labels.openReader}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="qxr__card" aria-labelledby="qxr-alerts">
        <div className="qxr__card-head">
          <h2 id="qxr-alerts" className="qxr__section-title">
            <LandingIcon name="bell" size={18} /> {labels.notifications}
            {unread > 0 ? <span className="qxr__badge">{unread}</span> : null}
          </h2>
          {data.alerts.length > 0 ? (
            <button
              type="button"
              className="qxr__link"
              aria-label={labels.markAllRead}
              onClick={() => {
                markLandingAlertsRead();
                onAlertsChanged();
              }}
            >
              <LandingIcon name="check" size={15} />
            </button>
          ) : null}
        </div>
        {data.alerts.length === 0 ? (
          <p className="qxr__empty">{labels.noNotifications}</p>
        ) : (
          <ul className="qxr__alerts">
            {data.alerts.map((a) => (
              <li key={a.id} data-read={a.read ? 'true' : 'false'}>
                <span>{a.text}</span>
                <time>{formatDate(a.at, interfaceTag)}</time>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
